/**
 * Pure helpers shared by the two admin app pages — App Registry (what the shell
 * can load) and App Onboarding (the intake review queue). The registry page
 * joins each registry entry with the onboarding submission it came from, by
 * appId, so a repo link and an intake link sit next to the live app.
 */

export const GITHUB_PREFIX = 'https://github.com/';

export type OnboardingStatus = 'submitted' | 'approved' | 'rejected';

/** The slice of an onboarding request the registry page needs. */
export interface IntakeRef {
  id: string;
  appId: string;
  repoUrl: string;
  pointOfContactEmail: string;
  pointOfContactName?: string;
  status: OnboardingStatus;
}

/** The slice of a registry entry the join and filters need. */
export interface RegistryRef {
  appId: string;
  displayName: string;
  externalUrl?: string;
  enabled: boolean;
  aiEnabled?: boolean;
  category?: string;
  ownerTeam?: string;
  requiredRole?: string[];
  requiredAlliance?: string[];
}

/** Where an app stands. `pending` = approved from onboarding but not yet enabled. */
export type AppStatus = 'live' | 'pending' | 'disabled';

export type AppRow<R extends RegistryRef> = R & {
  type: 'Hosted' | 'Linked';
  status: AppStatus;
  intake?: IntakeRef;
};

export const repoLabel = (url: string) => url.replace(GITHUB_PREFIX, '');

/** Deep link that opens one submission on the onboarding page. */
export const intakeHref = (requestId: string) => `/admin/onboarding?request=${encodeURIComponent(requestId)}`;

export function joinRegistry<R extends RegistryRef>(registry: R[], intakes: IntakeRef[]): AppRow<R>[] {
  const byAppId = new Map(intakes.map((i) => [i.appId, i]));
  return registry.map((r) => {
    const intake = byAppId.get(r.appId);
    const status: AppStatus = r.enabled ? 'live' : intake?.status === 'approved' ? 'pending' : 'disabled';
    return { ...r, type: r.externalUrl ? 'Linked' : 'Hosted', status, ...(intake ? { intake } : {}) };
  });
}

export interface AppFilters {
  text: string;
  /** '' = any. */
  status: AppStatus | '';
  type: 'Hosted' | 'Linked' | '';
  category: string;
  ai: 'yes' | 'no' | '';
}

export const NO_FILTERS: AppFilters = { text: '', status: '', type: '', category: '', ai: '' };

export function filterApps<R extends RegistryRef>(rows: AppRow<R>[], f: AppFilters): AppRow<R>[] {
  const q = f.text.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.status && r.status !== f.status) return false;
    if (f.type && r.type !== f.type) return false;
    if (f.category && (r.category ?? '') !== (f.category === 'none' ? '' : f.category)) return false;
    if (f.ai && Boolean(r.aiEnabled) !== (f.ai === 'yes')) return false;
    if (!q) return true;
    return [r.appId, r.displayName, r.ownerTeam, r.intake?.repoUrl, r.intake?.pointOfContactName, r.intake?.pointOfContactEmail]
      .some((v) => v?.toLowerCase().includes(q));
  });
}
